import { Alert, Chip, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InventoryDonationSourceStep from 'src/components/InventoryDonationSourceStep';
import { useInventoryItems } from 'src/hooks/useInventoryItems';

// Inventory picker for the edit donation forms. Shown when the team has items
// it can donate or the donation already uses some, like Create Donation.
// `locked` donations (already closed) show their items read-only.
export default function EditDonationInventoryPicker({
  enabled = true,
  locked = false,
  selectedItems = [],
  onToggle,
}) {
  const { t } = useTranslation('translation');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasEligibleInventory, setHasEligibleInventory] = useState(false);
  const inventoryQuery = useInventoryItems({
    enabled,
    status: 'active',
    search: debouncedSearch,
    limit: 5,
    offset: 0,
    eligibleForDonation: true,
  });
  const availableItems = inventoryQuery.data?.items ?? [];

  useEffect(() => {
    if (enabled && !debouncedSearch && inventoryQuery.data) {
      setHasEligibleInventory(inventoryQuery.data.items?.length > 0);
    }
  }, [enabled, debouncedSearch, inventoryQuery.data]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      300
    );
    return () => window.clearTimeout(timeout);
  }, [search]);

  const shouldShow =
    enabled &&
    (selectedItems.length > 0 ||
      hasEligibleInventory ||
      Boolean(inventoryQuery.isError));
  if (!shouldShow) return null;

  return (
    <Stack
      spacing={1}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        mb: 3,
      }}
    >
      <Typography variant="h6" fontWeight={700}>
        {t('donations.inventory-source.picker-title')}
      </Typography>
      {locked ? (
        <>
          <Alert severity="info">
            {t('donations.inventory-source.locked-closed')}
          </Alert>
          {selectedItems.length > 0 ? (
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {selectedItems.map((item) => (
                <Chip key={item.id} label={item.name} variant="outlined" />
              ))}
            </Stack>
          ) : null}
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('donations.inventory-source.edit-description')}
          </Typography>
          <InventoryDonationSourceStep
            compact
            error={inventoryQuery.isError}
            hasMore={inventoryQuery.data?.hasMore}
            items={[
              ...selectedItems,
              ...availableItems.filter(
                (item) =>
                  !selectedItems.some((selected) => selected.id === item.id)
              ),
            ]}
            loading={inventoryQuery.isLoading || inventoryQuery.isFetching}
            onRetry={inventoryQuery.refetch}
            onSearchChange={setSearch}
            onToggle={onToggle}
            search={search}
            selectedItems={selectedItems}
          />
        </>
      )}
    </Stack>
  );
}
